import { ISOCountryCode } from '@pmi.web/countries';

import { IAddress } from '../types/IAddress';

const API_ENDPOINT = {
  development: 'https://api.pm-international.net/addresslookup',
  production: 'https://api.pm-international.com/addresslookup',
};

interface IAddressRequest {
  readonly country: ISOCountryCode;
  readonly pageSize?: number;
  readonly env: 'development' | 'production';
}

interface IAddressSuggestionRequest extends IAddressRequest {
  readonly searchInput: string;
}

interface IUSZipAddressSuggestionRequest extends IAddressRequest {
  readonly postalCode: string;
}

interface IUSAddressSuggestionRequest extends Omit<IAddressRequest, 'country'> {
  readonly address: string;
  readonly postalCode: string;
  readonly administrativeArea: string;
  readonly locality: string;
}

interface IZipSearchResponse {
  readonly errorString: string;
  readonly resultCode: string;
  readonly results: ReadonlyArray<IAddress>;
}

interface IAddressResponse {
  readonly errorString: string;
  readonly resultCode: string;
  readonly results: ReadonlyArray<IAddress>;
}

export async function getAddressSuggestions({
  searchInput,
  country,
  pageSize = 10,
  env,
}: IAddressSuggestionRequest) {
  const response = await fetch(`${API_ENDPOINT[env]}/addressautocomplete`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      address: searchInput,
      country: country,
      maxRecords: pageSize,
    }),
  });

  if (response.ok === false) {
    return [];
  }

  try {
    const payload: IAddressResponse = await response.json();
    return payload.results;
  } catch (err) {
    return [];
  }
}

export async function getZipSuggestions({
  postalCode,
  country,
  pageSize = 10,
  env,
}: IUSZipAddressSuggestionRequest) {
  const urlSearchParams = new URLSearchParams();

  urlSearchParams.append('country', country);
  urlSearchParams.append('postalCode', postalCode);
  urlSearchParams.append('maxrecords', pageSize.toString());

  const response = await fetch(
    `${API_ENDPOINT[env]}/addressautocomplete/zipsearch?${urlSearchParams.toString()}`,
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (response.ok === false) {
    return [];
  }

  try {
    const payload: IZipSearchResponse = await response.json();
    return payload.results;
  } catch (err) {
    return [];
  }
}

export async function getUSAddressSuggestions({
  address,
  postalCode,
  administrativeArea,
  locality,
  pageSize = 10,
  env,
}: IUSAddressSuggestionRequest) {
  const urlSearchParams = new URLSearchParams();

  urlSearchParams.append('address', address);
  urlSearchParams.append('postalCode', postalCode);
  urlSearchParams.append('administrativearea', administrativeArea);
  urlSearchParams.append('locality', locality);
  urlSearchParams.append('country', ISOCountryCode.UnitedStatesAmerica);
  urlSearchParams.append('maxrecords', pageSize.toString());

  const response = await fetch(
    `${API_ENDPOINT[env]}/addressautocomplete/globalsearch?${urlSearchParams.toString()}`,
    {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (response.ok === false) {
    return [];
  }

  try {
    const payload: IAddressResponse = await response.json();
    return payload.results;
  } catch (err) {
    return [];
  }
}
